import { FC, KeyboardEvent } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  getNextNuggetToggleMode,
  getPreviousNuggetToggleMode,
  NuggetToggleMode,
} from 'src/utils/nuggetExpanded';
import * as S from './styles';

const msgs = defineMessages({
  off: {
    id: 'tri_switch_off',
    defaultMessage: 'Off',
  },
  on: {
    id: 'tri_switch_on',
    defaultMessage: 'On',
  },
  expanded: {
    id: 'tri_switch_expanded',
    defaultMessage: 'On and expanded',
  },
});

const VALUE_INDEX: Record<NuggetToggleMode, number> = {
  off: 0,
  on: 1,
  expanded: 2,
};

export interface TriSwitchProps {
  value: NuggetToggleMode;
  onChange: (value: NuggetToggleMode) => void;
  disabled?: boolean;
  'aria-label': string;
  'data-testid'?: string;
}

export const TriSwitch: FC<TriSwitchProps> = ({
  value,
  onChange,
  disabled,
  'aria-label': ariaLabel,
  'data-testid': testId,
}) => {
  const intl = useIntl();
  const valueText = intl.formatMessage(msgs[value]);

  const select = (nextValue: NuggetToggleMode) => {
    if (disabled || nextValue === value) {
      return;
    }

    onChange(nextValue);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      return;
    }

    if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      event.preventDefault();
      select(getNextNuggetToggleMode(value));
      return;
    }

    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      event.preventDefault();
      select(getPreviousNuggetToggleMode(value));
    }
  };

  const handleKeyUp = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      select(getNextNuggetToggleMode(value));
    }
  };

  return (
    <S.Container>
      <S.Button
        type="button"
        role="slider"
        aria-label={ariaLabel}
        aria-valuemin={0}
        aria-valuemax={2}
        aria-valuenow={VALUE_INDEX[value]}
        aria-valuetext={valueText}
        aria-disabled={disabled}
        disabled={disabled}
        data-testid={testId}
        data-value={value}
        $value={value}
        onClick={() => select(getNextNuggetToggleMode(value))}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
      >
        <S.Thumb />
      </S.Button>
    </S.Container>
  );
};
